import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, LogOut, RefreshCw, FileText, Bell, Filter } from "lucide-react";

const STATUSES = ["pending","confirmed","driver_assigned","in_progress","completed","cancelled"];

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      localStorage.setItem("lume_admin_token", data.token);
      nav("/admin/dashboard");
    } catch (e) {
      toast.error("Invalid credentials");
    } finally { setLoading(false); }
  };
  return (
    <div className="section-container py-24 max-w-md">
      <div className="lume-card p-8">
        <div className="eyebrow mb-3">Admin Portal</div>
        <h1 className="font-serif text-2xl text-white">Sign in</h1>
        <div className="mt-6 space-y-4">
          <div><label className="field-label">Email</label><input type="email" className="input-lume" value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="admin-email"/></div>
          <div><label className="field-label">Password</label><input type="password" className="input-lume" value={password} onChange={(e)=>setPassword(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&submit()} data-testid="admin-password"/></div>
          <button onClick={submit} disabled={loading} className="btn-gold w-full" data-testid="admin-login-btn">{loading ? "Signing in…" : "Sign in"}</button>
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [tab, setTab] = useState("reservations");
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(null);
  const [extras, setExtras] = useState({ additional_charges: 0, charges_description: "" });

  const { data: metrics } = useQuery({ queryKey: ["metrics"], queryFn: async () => (await api.get("/admin/metrics")).data });
  const { data: reservations = [], refetch } = useQuery({ queryKey: ["res", filter, q], queryFn: async () => (await api.get("/admin/reservations", { params: { status: filter || undefined, q: q || undefined }})).data });
  const { data: events = [] } = useQuery({ queryKey: ["events"], queryFn: async () => (await api.get("/admin/inquiries/events")).data });
  const { data: quotes = [] } = useQuery({ queryKey: ["quotes"], queryFn: async () => (await api.get("/admin/inquiries/quotes")).data });
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts"], queryFn: async () => (await api.get("/admin/contacts")).data });

  const statusMut = useMutation({
    mutationFn: async ({ rn, status, note }) => (await api.patch(`/admin/reservations/${rn}`, { status, admin_note: note || "" })).data,
    onSuccess: () => { qc.invalidateQueries({queryKey: ["res"]}); qc.invalidateQueries({queryKey: ["metrics"]}); toast.success("Reservation updated & customer notified."); setSelected(null); },
    onError: () => toast.error("Update failed."),
  });
  const reminderMut = useMutation({
    mutationFn: async (rn) => (await api.post(`/admin/reservations/${rn}/remind`)).data,
    onSuccess: () => toast.success("Reminder sent to customer."),
  });
  const invoiceMut = useMutation({
    mutationFn: async ({ rn, ...body }) => (await api.post(`/admin/reservations/${rn}/invoice`, body)).data,
    onSuccess: (inv) => { toast.success("Invoice generated & emailed."); qc.invalidateQueries({queryKey: ["res"]}); setInvoiceOpen(null); nav(`/invoice/${inv.invoice_number}`); },
    onError: () => toast.error("Invoice failed"),
  });

  const logout = () => { localStorage.removeItem("lume_admin_token"); nav("/admin"); };

  const tabs = [
    { k: "reservations", l: "Reservations" },
    { k: "referrals", l: "Referrals" },
    { k: "corporate", l: "Corporate" },
    { k: "settings", l: "Pricing" },
    { k: "events", l: "Event Inquiries" },
    { k: "quotes", l: "Quote Requests" },
    { k: "contacts", l: "Messages" },
  ];

  return (
    <div className="section-container py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Admin Dashboard</div>
          <h1 className="font-serif text-3xl text-white">Concierge Control</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={() => refetch()} className="btn-ghost" data-testid="refresh-btn"><RefreshCw size={14} className="inline mr-2"/>Refresh</button>
          <button onClick={logout} className="btn-ghost" data-testid="logout-btn"><LogOut size={14} className="inline mr-2"/>Sign out</button>
        </div>
      </div>

      {metrics && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {k: "Total Bookings", v: metrics.total_reservations},
            {k: "Active Rides", v: metrics.active},
            {k: "Pending", v: metrics.pending},
            {k: "Revenue (Completed)", v: fmtMoney(metrics.revenue_completed)},
          ].map(m => (
            <div key={m.k} className="lume-card p-5" data-testid={`metric-${m.k}`}>
              <div className="eyebrow">{m.k}</div>
              <div className="font-serif text-2xl text-white mt-2">{m.v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 flex gap-2 border-b border-white/5">
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-3 text-xs uppercase tracking-widest border-b-2 transition-colors ${tab===t.k ? "border-[color:var(--lume-gold)] text-white" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "reservations" && (
        <>
          <div className="mt-6 flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
              <input placeholder="Search # / name / email…" className="input-lume pl-9" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="search-input"/>
            </div>
            <div className="flex items-center gap-2"><Filter size={14} className="text-slate-500"/>
              <select className="input-lume w-auto" value={filter} onChange={(e)=>setFilter(e.target.value)} data-testid="status-filter">
                <option value="">All statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-4 lume-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-white/5">
                  <th className="p-4">Reservation</th><th className="p-4">Customer</th><th className="p-4">Service</th><th className="p-4">Date</th><th className="p-4">Vehicle</th><th className="p-4">Fare</th><th className="p-4">Status</th><th className="p-4">Payment</th><th className="p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map(r => (
                  <tr key={r.reservation_number} className="border-b border-white/5 hover:bg-white/2" data-testid={`res-row-${r.reservation_number}`}>
                    <td className="p-4 font-mono text-[color:var(--lume-gold-light)]">{r.reservation_number}</td>
                    <td className="p-4"><div className="text-white">{r.customer_name}</div><div className="text-xs text-slate-500">{r.customer_email}</div></td>
                    <td className="p-4 text-slate-300 capitalize">{r.service_type.replace(/-/g," ")}</td>
                    <td className="p-4 text-slate-300">{r.pickup_date} · {r.pickup_time}</td>
                    <td className="p-4 text-slate-300 text-xs">{r.vehicle_class_name}</td>
                    <td className="p-4 text-white">{fmtMoney(r.estimated_fare)}</td>
                    <td className="p-4"><span className={`status-pill status-${r.status}`}>{r.status.replace("_"," ")}</span></td>
                    <td className="p-4"><span className={`status-pill ${r.payment_status === "paid" ? "status-confirmed" : r.payment_status === "failed" || r.payment_status === "expired" ? "status-cancelled" : r.payment_status === "refunded" ? "status-completed" : "status-pending"}`} data-testid={`pay-status-${r.reservation_number}`}>{r.payment_status || "unpaid"}</span></td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button onClick={() => setSelected(r)} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`open-${r.reservation_number}`}>Manage</button>
                        <button onClick={() => reminderMut.mutate(r.reservation_number)} className="btn-ghost !py-1.5 !px-3 !text-xs" title="Send reminder" data-testid={`remind-${r.reservation_number}`}><Bell size={12}/></button>
                        <button onClick={() => setInvoiceOpen(r)} className="btn-ghost !py-1.5 !px-3 !text-xs" title="Invoice" data-testid={`invoice-${r.reservation_number}`}><FileText size={12}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {reservations.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-500">No reservations yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "events" && <SimpleList items={events} testid="events" fields={["event_type","event_date","passengers","customer_name","customer_email","customer_phone","vehicle_preference"]}/>}
      {tab === "quotes" && <SimpleList items={quotes} testid="quotes" fields={["service_type","pickup_location","destination","pickup_date","passengers","customer_name","customer_email","customer_phone"]}/>}
      {tab === "contacts" && <SimpleList items={contacts} testid="contacts" fields={["subject_category","name","email","phone","message"]}/>}
      {tab === "referrals" && <ReferralsTab/>}
      {tab === "corporate" && <CorporateTab/>}
      {tab === "settings" && <SettingsTab/>}

      {/* Manage reservation modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)"}} onClick={() => setSelected(null)}>
          <div className="lume-card p-6 max-w-lg w-full" onClick={(e)=>e.stopPropagation()} data-testid="manage-modal">
            <div className="eyebrow mb-2">Manage Reservation</div>
            <h3 className="font-serif text-xl text-white">{selected.reservation_number}</h3>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {STATUSES.map(s => (
                <button key={s} onClick={() => statusMut.mutate({ rn: selected.reservation_number, status: s })} data-testid={`set-status-${s}`}
                  className={`py-2 text-xs uppercase tracking-widest rounded-full border transition-colors ${selected.status===s ? "border-[color:var(--lume-gold)] text-[color:var(--lume-gold)]" : "border-white/10 text-slate-400 hover:border-white/30"}`}>
                  {s.replace("_"," ")}
                </button>
              ))}
            </div>
            {selected.admin_notes?.length > 0 && (
              <div className="mt-5">
                <div className="eyebrow mb-2">Notes</div>
                {selected.admin_notes.map((n,i)=><div key={i} className="text-sm text-slate-300 border-l border-[color:var(--lume-gold)]/30 pl-3 py-1">{n.note}</div>)}
              </div>
            )}
            <button onClick={()=>setSelected(null)} className="btn-ghost mt-6 w-full" data-testid="close-manage">Close</button>
          </div>
        </div>
      )}

      {/* Invoice modal */}
      {invoiceOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)"}} onClick={() => setInvoiceOpen(null)}>
          <div className="lume-card p-6 max-w-lg w-full" onClick={(e)=>e.stopPropagation()} data-testid="invoice-modal">
            <div className="eyebrow mb-2">Generate Invoice</div>
            <h3 className="font-serif text-xl text-white">{invoiceOpen.reservation_number}</h3>
            <div className="mt-4 text-sm text-slate-400">Base fare: <span className="text-white">{fmtMoney(invoiceOpen.estimated_fare)}</span></div>
            <div className="mt-4"><label className="field-label">Additional Charges</label><input type="number" step="0.01" className="input-lume" value={extras.additional_charges} onChange={(e)=>setExtras({...extras, additional_charges:+e.target.value})} data-testid="inv-extras"/></div>
            <div className="mt-4"><label className="field-label">Waiting Time (total minutes — 15 min grace included)</label><input type="number" min={0} className="input-lume" value={extras.wait_minutes || 0} onChange={(e)=>setExtras({...extras, wait_minutes:+e.target.value})} data-testid="inv-wait"/></div>
            <div className="mt-4"><label className="field-label">Description</label><input className="input-lume" value={extras.charges_description} onChange={(e)=>setExtras({...extras, charges_description:e.target.value})} placeholder="Wait time, tolls, etc." data-testid="inv-desc"/></div>
            <button onClick={() => invoiceMut.mutate({ rn: invoiceOpen.reservation_number, ...extras })} className="btn-gold w-full mt-6" data-testid="inv-generate-btn">Generate & Email Invoice</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SimpleList({ items, testid, fields }) {
  return (
    <div className="mt-6 space-y-3">
      {items.map((it, i) => (
        <div key={i} className="lume-card p-5" data-testid={`${testid}-item-${i}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-sm">
            {fields.map(f => it[f] && (
              <div key={f}>
                <div className="text-xs uppercase tracking-widest text-slate-500">{f.replace(/_/g," ")}</div>
                <div className="text-slate-200">{String(it[f])}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {items.length === 0 && <div className="lume-card p-10 text-center text-slate-500">No entries yet.</div>}
    </div>
  );
}

function SettingsTab() {
  const qc = useQueryClient();
  const { data: s } = useQuery({ queryKey: ["pricing-settings"], queryFn: async () => (await api.get("/admin/settings/pricing")).data });
  const [f, setF] = useState(null);
  useEffect(() => {
    if (s) setF({
      jfk: s.airport_fares?.JFK ?? 150, ewr: s.airport_fares?.EWR ?? 175, lga: s.airport_fares?.LGA ?? 110,
      tax: +(((s.sales_tax_rate ?? 0.08875) * 100).toFixed(3)), ref: s.referral_percent ?? 10,
      hr_sedan: s.hourly_rates?.sedan ?? "", hr_suv: s.hourly_rates?.suv ?? "", hr_van: s.hourly_rates?.van ?? "",
    });
  }, [s]);
  const save = useMutation({
    mutationFn: async () => (await api.patch("/admin/settings/pricing", {
      airport_fares: { JFK: +f.jfk, EWR: +f.ewr, LGA: +f.lga },
      sales_tax_rate: +f.tax / 100, referral_percent: +f.ref,
      hourly_rates: { sedan: f.hr_sedan === "" ? null : +f.hr_sedan, suv: f.hr_suv === "" ? null : +f.hr_suv, van: f.hr_van === "" ? null : +f.hr_van },
    })).data,
    onSuccess: () => { toast.success("Pricing settings saved."); qc.invalidateQueries({ queryKey: ["pricing-settings"] }); },
    onError: () => toast.error("Save failed."),
  });
  if (!f) return <div className="mt-6 text-slate-500">Loading…</div>;
  return (
    <div className="mt-6 lume-card p-6 max-w-xl">
      <div className="eyebrow mb-4">Airport Transfer Base Fares — Both Directions</div>
      <div className="grid grid-cols-3 gap-4">
        {[["jfk","JFK ↔ NYC"],["ewr","EWR ↔ NYC"],["lga","LGA ↔ NYC"]].map(([k, l]) => (
          <div key={k}><label className="field-label">{l}</label>
            <input type="number" step="0.01" className="input-lume" value={f[k]} onChange={(e)=>setF({...f,[k]:e.target.value})} data-testid={`fare-${k}`}/></div>
        ))}
      </div>
      <div className="eyebrow mt-6 mb-4">Taxes & Referral</div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="field-label">Sales Tax %</label><input type="number" step="0.001" className="input-lume" value={f.tax} onChange={(e)=>setF({...f,tax:e.target.value})} data-testid="tax-rate"/></div>
        <div><label className="field-label">Referral Discount %</label><input type="number" step="1" className="input-lume" value={f.ref} onChange={(e)=>setF({...f,ref:e.target.value})} data-testid="referral-pct"/></div>
      </div>
      <div className="eyebrow mt-6 mb-4">Hourly Service Rates ($/hour by vehicle category)</div>
      <div className="grid grid-cols-3 gap-4">
        {[["hr_sedan","Sedan"],["hr_suv","SUV"],["hr_van","Van"]].map(([k, l]) => (
          <div key={k}><label className="field-label">{l} / hour</label>
            <input type="number" step="0.01" placeholder="Not set" className="input-lume" value={f[k]} onChange={(e)=>setF({...f,[k]:e.target.value})} data-testid={`hourly-${k}`}/></div>
        ))}
      </div>
      <p className="mt-4 text-xs text-slate-500">Base fares are shown before sales tax. Tolls are always separate and may apply depending on the route. Hourly rates exclude tolls, parking, waiting time, and additional stops ($10/stop).</p>
      <button className="btn-gold mt-5" disabled={save.isPending} onClick={()=>save.mutate()} data-testid="pricing-save-btn">{save.isPending ? "Saving…" : "Save Pricing"}</button>
    </div>
  );
}

export default function Admin() {
  const token = localStorage.getItem("lume_admin_token");
  const path = window.location.pathname;
  if (path === "/admin/dashboard" && !token) return <Navigate to="/admin" replace/>;
  if (path === "/admin/dashboard") return <Dashboard/>;
  if (token) return <Navigate to="/admin/dashboard" replace/>;
  return <Login/>;
}

function ReferralsTab() {
  const qc = useQueryClient();
  const { data: pending = [] } = useQuery({ queryKey: ["ref-pending"], queryFn: async () => (await api.get("/admin/referrals/pending")).data });
  const { data: credits = [] } = useQuery({ queryKey: ["ref-credits"], queryFn: async () => (await api.get("/admin/referrals/credits")).data });
  const [linkState, setLinkState] = useState({});
  const linkMut = useMutation({
    mutationFn: async ({ reservation_number, referrer_email }) => (await api.post("/admin/referrals/link", { reservation_number, referrer_email })).data,
    onSuccess: () => { toast.success("Referrer linked. 10% off unlocks when their ride is completed."); qc.invalidateQueries({queryKey:["ref-pending"]}); qc.invalidateQueries({queryKey:["ref-credits"]}); },
    onError: () => toast.error("Could not link referrer"),
  });

  return (
    <div className="mt-6 space-y-8">
      <div>
        <div className="eyebrow mb-3">Bookings Awaiting Referral Link</div>
        <div className="lume-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-white/5">
              <th className="p-4">Reservation</th><th className="p-4">Rider</th><th className="p-4">Referred By</th><th className="p-4">Status</th><th className="p-4">Referrer Email</th><th className="p-4">Action</th>
            </tr></thead>
            <tbody>
              {pending.map(r => (
                <tr key={r.reservation_number} className="border-b border-white/5" data-testid={`ref-row-${r.reservation_number}`}>
                  <td className="p-4 font-mono text-[color:var(--lume-gold-light)]">{r.reservation_number}</td>
                  <td className="p-4"><div className="text-white">{r.customer_name}</div><div className="text-xs text-slate-500">{r.customer_email}</div></td>
                  <td className="p-4 text-slate-300">{r.referred_by}</td>
                  <td className="p-4"><span className={`status-pill status-${r.status}`}>{r.status.replace("_"," ")}</span>{r.credited && <span className="ml-2 status-pill status-completed">credited</span>}</td>
                  <td className="p-4">
                    {r.linked_email
                      ? <span className="text-emerald-400 text-xs">{r.linked_email}</span>
                      : <input className="input-lume !py-2" placeholder="referrer@email.com" value={linkState[r.reservation_number]||""} onChange={(e)=>setLinkState({...linkState, [r.reservation_number]:e.target.value})} data-testid={`ref-email-${r.reservation_number}`}/>}
                  </td>
                  <td className="p-4">
                    {!r.linked_email && <button className="btn-outline-gold !py-1.5 !px-3 !text-xs" onClick={()=>linkMut.mutate({reservation_number:r.reservation_number, referrer_email: linkState[r.reservation_number]||""})} data-testid={`ref-link-${r.reservation_number}`}>Link</button>}
                  </td>
                </tr>
              ))}
              {pending.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No pending referrals.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <div className="eyebrow mb-3">Unlocked Credits</div>
        <div className="lume-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-white/5">
              <th className="p-4">Referrer</th><th className="p-4">Referred Ride</th><th className="p-4">Discount</th><th className="p-4">Status</th><th className="p-4">Unlocked</th>
            </tr></thead>
            <tbody>
              {credits.map((c,i) => (
                <tr key={i} className="border-b border-white/5" data-testid={`credit-row-${i}`}>
                  <td className="p-4 text-white">{c.referrer_email}</td>
                  <td className="p-4 font-mono text-[color:var(--lume-gold-light)]">{c.referred_reservation}</td>
                  <td className="p-4 text-[color:var(--lume-gold-light)]">{c.percent ? `${c.percent}% Off` : fmtMoney(c.amount || 0)}</td>
                  <td className="p-4"><span className="status-pill status-confirmed">{c.status}</span></td>
                  <td className="p-4 text-slate-400 text-xs">{new Date(c.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {credits.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No credits unlocked yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CorporateTab() {
  const { data: accounts = [] } = useQuery({ queryKey: ["corp-list"], queryFn: async () => (await api.get("/admin/corporate")).data });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const nav = useNavigate();
  const monthMut = useMutation({
    mutationFn: async ({ company_id }) => (await api.post(`/admin/corporate/${company_id}/invoice-month`, { month })).data,
    onSuccess: (inv) => { toast.success(inv.settlement === "charged" ? "Invoice generated & card charged automatically." : inv.settlement === "charge_failed" ? "Invoice generated — card charge failed, emailed for manual payment." : "Monthly invoice generated & emailed."); nav(`/invoice/${inv.invoice_number}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not generate monthly invoice."),
  });
  return (
    <div className="mt-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <label className="field-label !mb-0">Billing Month</label>
        <input type="month" className="input-lume w-auto" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="corp-month-input"/>
      </div>
      <div className="lume-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-white/5">
          <th className="p-4">Company</th><th className="p-4">Contact</th><th className="p-4">Email</th><th className="p-4">Phone</th><th className="p-4">Reservations</th><th className="p-4">Billing</th><th className="p-4">Monthly Invoice</th>
        </tr></thead>
        <tbody>
          {accounts.map(a => (
            <tr key={a.id} className="border-b border-white/5" data-testid={`corp-row-${a.id}`}>
              <td className="p-4 text-white font-serif">{a.company_name}</td>
              <td className="p-4 text-slate-300">{a.contact_name}</td>
              <td className="p-4 text-slate-300">{a.email}</td>
              <td className="p-4 text-slate-300">{a.phone||"—"}</td>
              <td className="p-4 text-[color:var(--lume-gold-light)] font-mono">{a.reservation_count}</td>
              <td className="p-4 text-xs">
                {a.stripe_default_pm
                  ? <span className="text-slate-300 capitalize">{a.card_brand} ···· {a.card_last4}{a.auto_settle && <span className="ml-2 status-pill status-confirmed">auto</span>}</span>
                  : <span className="text-slate-500">invoice-only</span>}
              </td>
              <td className="p-4">
                <button className="btn-outline-gold !py-1.5 !px-3 !text-xs" disabled={monthMut.isPending}
                  onClick={() => monthMut.mutate({ company_id: a.id })} data-testid={`corp-invoice-${a.id}`}>
                  Generate {month}
                </button>
              </td>
            </tr>
          ))}
          {accounts.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No corporate accounts yet. Share <span className="font-mono text-slate-300">/corporate</span> to onboard.</td></tr>}
        </tbody>
      </table>
      </div>
    </div>
  );
}
